import React, { useEffect, useRef, useState } from 'react';
import ReportIssueBody from './ReportIssueBody';
import { CannotOpenFile } from './CannotOpenFile';
import { useUiAdminConfigsContext, useMsGraphContext } from '@cs-cms/state-management';
import { CpsHelpCenterItemState, CpsReportIssueState } from '@cs-cms/types';
import { useWindowDimensions, ElementDimensions } from '../../hooks/useWindowDimensions';
import { HELP_CENTER_MENU_ITEM_TYPE } from '../../types/HelpCenterMenuItemType';

interface HelpCenterDocumentProps {
  selectedMenuItem?: CpsHelpCenterItemState;
}

const defaultPage = 'Doc';
const embedPage = 'embed';
const powerPointExtensions = ['.pptx', '.pptm', '.ppt', '.pot', '.potm', '.ppsx', '.ppsm', '.pps'];

const powerPointView = 'interactivepreview';
const defaultView = 'embedview';

const extractContentBeforeLayouts = (url: string): string | null => {
  const regex = /^(.*)(?=\/_layouts\/15\/)/;
  const match = url.match(regex);
  return match ? match[1] : null;
};

const extractUniqueId = (url: string): string | null => {
  const regex = /[?&]UniqueId=([^&]+)/;
  const match = url.match(regex);
  return match ? decodeURIComponent(match[1]) : null;
};

export const HelpCenterDocument: React.FC<HelpCenterDocumentProps> = props => {
  const ref = useRef<HTMLDivElement>(null);
  const windowDimensions = useWindowDimensions();
  const [helpCenterDimensions, setHelpCenterDimensions] = useState<ElementDimensions>({ width: 0, height: 0 });

  useEffect(() => {
    if (ref && ref.current) {
      const paddingSize = 50;
      setHelpCenterDimensions({
        height: document.body.offsetHeight - ref.current.getBoundingClientRect().top - paddingSize,
        width: ref.current.getBoundingClientRect().width
      });
    }
  }, [windowDimensions]);

  const { uiAdminConfigState } = useUiAdminConfigsContext();
  const reportIssueState = uiAdminConfigState.reportIssue as CpsReportIssueState;

  const { driveItem, setMsGraphProps, isDriveItemLoading } = useMsGraphContext();

  useEffect(() => {
    setMsGraphProps(prev => ({
      ...prev,
      fileUrl: props.selectedMenuItem?.url
    }));
  }, [props.selectedMenuItem?.url, setMsGraphProps]);

  const getSrcLink = () => {
    if (!driveItem) {
      return undefined;
    }
    const serverRelativeUrl = extractContentBeforeLayouts(driveItem['@microsoft.graph.downloadUrl']);
    const uniqueId = extractUniqueId(driveItem['@microsoft.graph.downloadUrl']);
    if (driveItem.name.endsWith('.pdf') || driveItem.name.endsWith('.txt')) {
      return `${serverRelativeUrl}/_layouts/15/${embedPage}.aspx?UniqueId=${uniqueId}`;
    }
    const currentAction = powerPointExtensions.some(el => driveItem.name.endsWith(el)) ? powerPointView : defaultView;
    return `${serverRelativeUrl}/_layouts/15/${defaultPage}.aspx?sourcedoc=${uniqueId}&action=${currentAction}`;
  };

  const renderContent = () => {
    if (
      props.selectedMenuItem?.id === HELP_CENTER_MENU_ITEM_TYPE.RAPORT_ISSUE &&
      reportIssueState &&
      reportIssueState.enabled
    ) {
      return <ReportIssueBody reportIssueState={reportIssueState} />;
    }

    if (driveItem) {
      return (
        <iframe
          style={{ height: helpCenterDimensions.height, width: helpCenterDimensions.width }}
          src={getSrcLink()}
          id="documenttemplateframe"
        />
      );
    }

    if (!isDriveItemLoading) {
      return <CannotOpenFile />;
    }

    return null;
  };

  return <div ref={ref}>{renderContent()}</div>;
};
